import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type CallbackMode = "admin_consent" | "user_auth" | "error" | "loading";

export interface CallbackSuccessResult {
  mode: "admin_consent" | "user_auth";
  status: "granted" | "connected";
  redirect_to?: string;
  user?: { display_name?: string; email?: string };
  scopes?: string[];
  scopes_tier?: string;
  connection_level?: "user" | "organization";
}

export interface CallbackErrorResult {
  mode: "error";
  error: string;
  error_description?: string;
  code?: string;
}

export type CallbackResult = CallbackSuccessResult | CallbackErrorResult;

interface UseMs365CallbackReturn {
  state: CallbackMode | "loading" | "success" | "error";
  result: CallbackSuccessResult | null;
  error: string | null;
}

// Module-level cache so that React StrictMode double-mounts (or any other
// rapid remount) share the same callback request instead of firing it twice.
const inflight = new Map<string, Promise<CallbackResult>>();

/** @internal Exported for tests only. */
export function __resetInflightForTests(): void {
  inflight.clear();
}

export function useMs365Callback(): UseMs365CallbackReturn {
  const { t } = useTranslation();
  const [state, setState] = useState<UseMs365CallbackReturn["state"]>("loading");
  const [result, setResult] = useState<CallbackSuccessResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const stateParam = params.get("state");
    const adminConsent = params.get("admin_consent")?.toLowerCase() === "true";
    const tenant = params.get("tenant");
    const msError = params.get("error");
    const msErrorDescription = params.get("error_description");

    if (msError) {
      setState("error");
      setError(msErrorDescription || msError);
      return;
    }

    // Microsoft redirects successful tenant-wide admin consent without an
    // authorization code: ?admin_consent=True&tenant=<guid>&state=<state>.
    // A delegated user-auth callback still requires the usual code + state.
    const isAdminConsentCallback = adminConsent && !params.has("code");

    if (!stateParam || (!isAdminConsentCallback && !code)) {
      setState("error");
      setError(t("connections:callback.missingCodeOrState"));
      return;
    }

    const onboardingToken = params.get("onboarding_token");
    const key = isAdminConsentCallback
      ? `admin_consent:${tenant || ""}:${stateParam}`
      : `${code}:${stateParam}:${onboardingToken || ""}`;

    const payload = isAdminConsentCallback
      ? {
          admin_consent: true,
          ...(tenant && { tenant }),
          state: stateParam,
        }
      : {
          code: code!,
          state: stateParam,
          ...(onboardingToken && { onboarding_token: onboardingToken }),
        };

    let promise = inflight.get(key);
    if (!promise) {
      promise = (async (): Promise<CallbackResult> => {
        const res = await fetch("/api/ms365/callback", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          return {
            mode: "error",
            error: data.error || data.error_description || data.detail || t("connections:callback.failed", { status: res.status }),
            error_description: data.error_description,
            code: data.code,
          };
        }

        return data as CallbackResult;
      })();
      inflight.set(key, promise);
    }

    promise
      .then((data) => {
        if (!mountedRef.current) return;

        if (data.mode === "error") {
          setState("error");
          const fallback = data.error_description || data.error;
          setError(data.code
            ? t(`connections:ms365.errors.${data.code}`, { defaultValue: fallback })
            : fallback);
        } else {
          setResult(data as CallbackSuccessResult);
          setState("success");
        }
      })
      .catch((err: any) => {
        if (!mountedRef.current) return;
        setState("error");
        setError(err?.message ?? t("connections:callback.network"));
      });
  }, [t]);

  return { state, result, error };
}
